import {
    Add,
    Cancel,
    DeleteOutline,
    DeleteOutlineOutlined,
    Edit,
    Groups,
    PersonAdd,
    Videocam,
} from '@mui/icons-material';
import { Autocomplete, Avatar, Box, Button, IconButton, Skeleton, Stack, TextField, Typography } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { type Control, Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import ModalV2 from '/@/components/bayer/modal-v2';
import SelectCheckbox from '/@/components/bayer/select-checkbox';
import ChipLabel, { CHIP_COLORS } from '/@/components/chip-label';
import type { ActivityAttendance, ActivityModality, ActivityType, ActivityWrite } from '/@/hooks/activities';
import { useActivityById, useCreateActivity, useUpdateActivity } from '/@/hooks/activities';
import { type UserNode, useAllUserNodesLookup, useSquad } from '/@/hooks/nodes';
import { useIsValidatedSquad } from '/@/hooks/session';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { getInitials, ROLE_LABELS } from '../../constants';

type FormStep = 'details' | 'participants';

type ParticipantFormValue = {
    userNode: number | null;
    otherName?: string;
    squad?: string | null;
    attendance: ActivityAttendance;
    notes: { value: string }[];
};

type ActivityFormValues = {
    activityType: ActivityType | '';
    name: string;
    date: Dayjs | null;
    modality: ActivityModality | '';
    place: string;
    topics: { value: string }[];
    speakers: number[];
    otherSpeakers: { value: string }[];
    budget: string;
    note: string;
    participants: ParticipantFormValue[];
};

const EMPTY_VALUES: ActivityFormValues = {
    activityType: '',
    name: '',
    date: null,
    modality: '',
    place: '',
    topics: [],
    speakers: [],
    otherSpeakers: [],
    budget: '',
    note: '',
    participants: [],
};

const TYPE_OPTIONS: { value: ActivityType; label: string; description: string }[] = [
    {
        value: 'entrenamiento',
        label: _('Entrenamiento'),
        description: _('Jornadas, capacitaciones y talleres técnicos.'),
    },
    {
        value: 'generacion_demanda',
        label: _('Generación de demanda'),
        description: _('Presentaciones de híbridos, ensayos y prospección de clientes.'),
    },
];

const MODALITY_CHIP_OPTIONS: { value: ActivityModality; label: string; icon: React.ReactNode }[] = [
    { value: 'presencial', label: _('Presencial'), icon: <Groups sx={{ width: 18, height: 18 }} /> },
    { value: 'virtual', label: _('Virtual'), icon: <Videocam sx={{ width: 18, height: 18 }} /> },
];

const ATTENDANCE_CHIP_OPTIONS: { value: ActivityAttendance; label: string; selectedBg: string }[] = [
    { value: 'si', label: _('Sí'), selectedBg: '#EEFFEF' },
    { value: 'parcial', label: _('Parcial'), selectedBg: '#EEFFEF' },
    { value: 'no', label: _('No'), selectedBg: '#FFE4CD' },
];

/** Sentinel id for the synthetic "typed text, no squad match" speaker option; real nodes always have a positive backend id. */
const OTROS_SPEAKER_ID = -1;

/** Falls back to 'gray' for node types without a dedicated chip color (e.g. grower, squad, other). */
const getNodeChipType = (type: UserNode['type']): keyof typeof CHIP_COLORS =>
    type in CHIP_COLORS ? (type as keyof typeof CHIP_COLORS) : 'gray';

const FormSection = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
        <Typography sx={{ fontSize: '16px', letterSpacing: '0.15px', fontWeight: 700 }} color='primary'>
            {label}
        </Typography>
        {children}
    </Stack>
);

const Tag = ({ label, onDelete }: { label: string; onDelete: () => void }) => (
    <Box
        sx={{
            bgcolor: '#E7F8FF',
            color: 'primary.main',
            borderRadius: '4px',
            pl: 1.5,
            pr: 1,
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '14px',
        }}
    >
        {label}
        <Cancel sx={{ width: 18, height: 18, opacity: 0.8, cursor: 'pointer' }} onClick={onDelete} />
    </Box>
);

const TagsSection = ({
    label,
    countLabel,
    tags,
    onRemove,
    onClearAll,
    children,
}: {
    label: string;
    countLabel: string;
    tags: { id: string; label: string }[];
    onRemove: (index: number) => void;
    onClearAll: () => void;
    children: React.ReactNode;
}) => (
    <FormSection label={label}>
        <Stack spacing={1} sx={{ width: '100%' }}>
            {tags.length > 0 && (
                <>
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Typography sx={{ fontSize: '12px', color: 'rgba(16, 56, 79, 0.73)' }}>{countLabel}</Typography>
                        <Stack
                            direction='row'
                            spacing={0.5}
                            alignItems='center'
                            sx={{ cursor: 'pointer' }}
                            onClick={onClearAll}
                        >
                            <DeleteOutline sx={{ width: 14, height: 14 }} />
                            <Typography sx={{ fontSize: '12px' }} color='primary'>
                                {_('Borrar todo')}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                        {tags.map((tag, index) => (
                            <Tag key={tag.id} label={tag.label} onDelete={() => onRemove(index)} />
                        ))}
                    </Stack>
                </>
            )}
            {children}
        </Stack>
    </FormSection>
);

const ParticipantCard = ({
    control,
    index,
    node,
    otherName,
    squad,
    onRemove,
}: {
    control: Control<ActivityFormValues>;
    index: number;
    node: UserNode | null;
    otherName?: string;
    squad?: string | null;
    onRemove: () => void;
}) => {
    const displayName = node?.name ?? otherName ?? '';
    const notesFieldArray = useFieldArray({ control, name: `participants.${index}.notes` });
    const notes = useWatch({ control, name: `participants.${index}.notes` }) ?? [];
    const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null);

    const handleAddNote = () => {
        const newIndex = notesFieldArray.fields.length;
        notesFieldArray.append({ value: '' });
        setEditingNoteIndex(newIndex);
    };

    const handleBlurNote = (noteIndex: number, value: string) => {
        if (!value.trim()) {
            notesFieldArray.remove(noteIndex);
        }
        setEditingNoteIndex(null);
    };

    return (
        <Box
            sx={{
                border: '1px solid rgba(16, 56, 79, 0.12)',
                borderRadius: '8px',
                boxShadow: '0px 1px 0.5px rgba(0,0,0,0.14), 0px 2px 0.5px rgba(0,0,0,0.12)',
                p: 2,
            }}
        >
            <Stack direction='row' justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Stack direction='row' spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ minWidth: 0 }}>
                    <Avatar sx={[{ background: CHIP_COLORS.gray.background, width: 40, height: 40, flexShrink: 0 }]}>
                        <Typography variant='caption' fontWeight='bold' color='primary'>
                            {getInitials(displayName)}
                        </Typography>
                    </Avatar>
                    <Stack spacing={0.5} direction='column' sx={{ minWidth: 0 }}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <ChipLabel
                                type={node ? getNodeChipType(node.type) : 'gray'}
                                label={node ? ROLE_LABELS[node.type] : _('Otros')}
                            />
                            {squad && (
                                <Typography
                                    variant='caption'
                                    color='primary.mui.inactive'
                                    fontWeight={500}
                                    sx={{
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {squad}
                                </Typography>
                            )}
                        </Stack>
                        <Typography sx={{ fontWeight: 700, wordBreak: 'break-word' }} color='primary'>
                            {displayName}
                        </Typography>
                    </Stack>
                </Stack>
                <IconButton onClick={onRemove} sx={{ flexShrink: 0 }}>
                    <DeleteOutlineOutlined
                        sx={{ cursor: 'pointer', width: 18, height: 18, color: 'primary.onSurface' }}
                    />
                </IconButton>
            </Stack>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}
                sx={{ mt: 1.5 }}
            >
                <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap>
                    <Typography sx={{ fontSize: '14px', color: 'rgba(16, 56, 79, 0.6)' }}>{_('Asistencia')}</Typography>
                    <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                        <Controller
                            name={`participants.${index}.attendance`}
                            control={control}
                            render={({ field }) => (
                                <>
                                    {ATTENDANCE_CHIP_OPTIONS.map((option) => (
                                        <Box
                                            key={option.value}
                                            onClick={() => field.onChange(option.value)}
                                            sx={{
                                                border: '1px solid rgba(16, 56, 79, 0.12)',
                                                borderRadius: '100px',
                                                px: 1.5,
                                                py: 0.5,
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                bgcolor:
                                                    field.value === option.value ? option.selectedBg : 'transparent',
                                            }}
                                            color='primary'
                                        >
                                            {option.label}
                                        </Box>
                                    ))}
                                </>
                            )}
                        />
                    </Stack>
                </Stack>
                <Button
                    variant='text'
                    color='primary'
                    onClick={handleAddNote}
                    startIcon={<Add sx={{ width: 14, height: 14 }} />}
                >
                    {_('Agregar una nota')}
                </Button>
            </Stack>

            {notesFieldArray.fields.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                    {notesFieldArray.fields.map((noteField, noteIndex) =>
                        editingNoteIndex === noteIndex ? (
                            <Controller
                                key={noteField.id}
                                name={`participants.${index}.notes.${noteIndex}.value`}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        autoFocus
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder={_('Nota')}
                                        onBlur={() => handleBlurNote(noteIndex, field.value)}
                                    />
                                )}
                            />
                        ) : (
                            <Stack
                                key={noteField.id}
                                direction='row'
                                justifyContent='space-between'
                                alignItems='center'
                                sx={{
                                    bgcolor: '#F5F5F5',
                                    borderRadius: '4px',
                                    p: 1,
                                    cursor: 'pointer',
                                }}
                                onClick={() => setEditingNoteIndex(noteIndex)}
                            >
                                <Typography sx={{ fontSize: '12px' }} color='primary'>
                                    {notes[noteIndex]?.value}
                                </Typography>
                                <Edit sx={{ width: 18, height: 18 }} color='primary' />
                            </Stack>
                        )
                    )}
                </Stack>
            )}
        </Box>
    );
};

const ActivityFormModal = ({
    open,
    onClose,
    activityId,
}: {
    open: boolean;
    onClose: () => void;
    activityId?: number;
}) => {
    const isEditMode = Boolean(activityId);
    const isValidatedSquad = useIsValidatedSquad();
    const { data: activity } = useActivityById(activityId, { enabled: open && isEditMode });
    const { allNodesById, isLoading: isNodesLoading } = useAllUserNodesLookup();
    const { data: squadNodes, isLoading: isSquadLoading } = useSquad();
    // Squad members can fall outside the current user's own node hierarchy (e.g. peers
    // under the same Squad Coach), so merge them in for name/role lookups.
    const nodesById = useMemo(() => {
        const map = new Map(allNodesById);
        for (const node of squadNodes || []) {
            if (!map.has(node.id)) map.set(node.id, node);
        }
        return map;
    }, [allNodesById, squadNodes]);
    const nodeOptions = useMemo(() => Array.from(nodesById.values()), [nodesById]);

    const [step, setStep] = useState<FormStep>('details');
    const [topicInput, setTopicInput] = useState('');
    const [speakerInput, setSpeakerInput] = useState('');

    const form = useForm<ActivityFormValues>({
        mode: 'onChange',
        defaultValues: EMPTY_VALUES,
    });
    const { control, handleSubmit, formState, reset, watch, setValue } = form;

    const topicsFieldArray = useFieldArray({ control, name: 'topics' });
    const otherSpeakersFieldArray = useFieldArray({ control, name: 'otherSpeakers' });
    const participantsFieldArray = useFieldArray({ control, name: 'participants' });
    const modality = watch('modality');
    const participants = watch('participants');

    useEffect(() => {
        if (!open) return;
        setStep('details');
        setTopicInput('');
        if (isEditMode && activity && activity.id === activityId) {
            reset({
                activityType: activity.activityType,
                name: activity.name,
                date: dayjs(activity.date),
                modality: activity.modality,
                place: activity.place || '',
                topics: (activity.topics || []).map((value) => ({ value })),
                speakers: activity.speakers.map((speaker) => speaker.id),
                otherSpeakers: (activity.otherSpeakers || []).map((value) => ({ value })),
                budget: activity.budget ? String(activity.budget) : '',
                note: activity.note || '',
                participants: activity.participants.map((participant) => ({
                    userNode: participant.userNode?.id ?? null,
                    otherName: participant.otherName,
                    squad: participant.squad,
                    attendance: participant.attendance,
                    notes: (participant.notes || []).map((value) => ({ value })),
                })),
            });
        } else if (!isEditMode) {
            reset(EMPTY_VALUES);
        }
    }, [open, isEditMode, activity, activityId, reset]);

    useEffect(() => {
        if (modality === 'virtual') {
            setValue('place', '', { shouldValidate: true });
        }
    }, [modality, setValue]);

    const createActivityMutation = useCreateActivity();
    const updateActivityMutation = useUpdateActivity(activityId ?? 0);
    const isMutating = createActivityMutation.isMutating || updateActivityMutation.isMutating;

    const handleAddTopic = () => {
        const value = topicInput.trim();
        if (!value) return;
        topicsFieldArray.append({ value });
        setTopicInput('');
    };

    const addedParticipantIds = useMemo(
        () => new Set(participants.map((p) => p.userNode).filter((id): id is number => id !== null)),
        [participants]
    );

    const squadSearchItems = useMemo(
        () =>
            (squadNodes || []).map((node) => ({
                id: node.id,
                label: node.name,
                chip: <ChipLabel type={getNodeChipType(node.type)} label={ROLE_LABELS[node.type]} size='sm' />,
            })),
        [squadNodes]
    );

    const toggleParticipant = (node: UserNode) => {
        const index = participants.findIndex((p) => p.userNode === node.id);
        if (index >= 0) {
            participantsFieldArray.remove(index);
        } else {
            participantsFieldArray.append({ userNode: node.id, attendance: 'si', notes: [] });
        }
    };

    const handleAddFullTeam = () => {
        const newParticipants = (squadNodes || [])
            .filter((node) => !addedParticipantIds.has(node.id))
            .map((node) => ({ userNode: node.id, attendance: 'si' as ActivityAttendance, notes: [] }));
        participantsFieldArray.append(newParticipants);
    };

    const handleAddOtherParticipant = (name: string) => {
        participantsFieldArray.append({ userNode: null, otherName: name, attendance: 'si', notes: [] });
    };

    const handleAddOtherSpeaker = (name: string) => {
        otherSpeakersFieldArray.append({ value: name });
        setSpeakerInput('');
    };

    const attendanceTally = {
        si: participants.filter((p) => p.attendance === 'si').length,
        parcial: participants.filter((p) => p.attendance === 'parcial').length,
        no: participants.filter((p) => p.attendance === 'no').length,
    };

    const attendanceSummary = [
        {
            label: _('Si'),
            value: attendanceTally.si,
            color: '#01830C',
        },
        {
            label: _('Parcial'),
            value: attendanceTally.parcial,
            color: '#01830C',
        },
        {
            label: _('No'),
            value: attendanceTally.no,
            color: '#B85302',
        },
    ];

    const onSubmit = async (data: ActivityFormValues) => {
        const payload: ActivityWrite = {
            activityType: data.activityType as ActivityType,
            name: data.name,
            date: (data.date as Dayjs).format('YYYY-MM-DD'),
            modality: data.modality as ActivityModality,
            place: data.modality === 'presencial' ? data.place : '',
            topics: data.topics.map((topic) => topic.value),
            speakers: data.speakers,
            otherSpeakers: data.otherSpeakers.map((speaker) => speaker.value),
            budget: data.budget !== '' ? data.budget : null,
            note: data.note,
            participants: data.participants.map((participant) => ({
                userNode: participant.userNode,
                otherName: participant.otherName,
                attendance: participant.attendance,
                notes: participant.notes.map((note) => note.value).filter(Boolean),
            })),
        };

        try {
            if (isEditMode && activityId) {
                await updateActivityMutation.trigger(payload);
                enqueueSnackbar(_('Actividad actualizada correctamente'), { variant: 'success' });
            } else {
                await createActivityMutation.trigger(payload);
                enqueueSnackbar(_('Actividad creada correctamente'), { variant: 'success' });
            }
            onClose();
        } catch {
            enqueueSnackbar(isEditMode ? _('Error al actualizar la actividad') : _('Error al crear la actividad'), {
                variant: 'error',
            });
        }
    };

    const { isValid } = formState;
    const topics = watch('topics');
    const speakers = watch('speakers');
    const otherSpeakers = watch('otherSpeakers');
    const place = watch('place');
    const detailsValid =
        isValid &&
        topics.length > 0 &&
        (speakers.length > 0 || otherSpeakers.length > 0) &&
        (modality !== 'presencial' || place.trim() !== '');

    // Squad Coach can view and edit activities but cannot create new ones.
    if (!isEditMode && isValidatedSquad) {
        return null;
    }

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            goBack={step === 'participants' ? () => setStep('details') : onClose}
            title={
                step === 'participants'
                    ? _('Participantes y asistencia')
                    : isEditMode
                      ? _('Editar actividad')
                      : _('Nueva actividad')
            }
            maxWidth='sm'
            bodyProps={{
                alignItems: 'stretch',
                textAlign: 'left',
                sx: { p: 3, maxHeight: '70vh', overflow: 'auto' },
            }}
            actions={
                step === 'details' ? (
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        <Typography sx={{ fontSize: '12px' }} color='text.secondary'>
                            {_('Todos los datos son requeridos para continuar, salvo presupuesto y nota.')}
                        </Typography>
                        <Stack direction='row' spacing={1} justifyContent='flex-end'>
                            <Button
                                variant='contained'
                                color='secondary'
                                disabled={!detailsValid}
                                onClick={() => setStep('participants')}
                            >
                                {_('Continuar')}
                            </Button>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ width: '100%' }}>
                        <Button
                            type='submit'
                            variant='contained'
                            color='secondary'
                            loading={isMutating}
                            onClick={handleSubmit(onSubmit)}
                        >
                            {_('Guardar actividad')}
                        </Button>
                    </Stack>
                )
            }
        >
            {step === 'details' ? (
                <Stack spacing={3} sx={{ width: '100%' }}>
                    <FormSection label={_('Tipo de actividad')}>
                        <Controller
                            name='activityType'
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
                                    {TYPE_OPTIONS.map((option) => {
                                        const selected = field.value === option.value;
                                        return (
                                            <Box
                                                key={option.value}
                                                onClick={() => field.onChange(option.value)}
                                                sx={{
                                                    flex: 1,
                                                    p: 2,
                                                    borderRadius: '8px',
                                                    border: '1px solid',
                                                    borderColor: selected ? '#DE0043' : 'rgba(16, 56, 79, 0.12)',
                                                    bgcolor: selected ? '#FFDCE4' : 'white',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '20px', fontWeight: 500 }} color='primary'>
                                                    {option.label}
                                                </Typography>
                                                <Typography sx={{ fontSize: '14px', color: 'rgba(16, 56, 79, 0.73)' }}>
                                                    {option.description}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        />
                    </FormSection>

                    <FormSection label={_('Fecha')}>
                        <Controller
                            name='date'
                            control={control}
                            rules={{ required: true }}
                            render={({ field, fieldState: { error } }) => (
                                <MuiDatePicker
                                    {...field}
                                    slotProps={{ textField: { fullWidth: true, error: !!error } }}
                                />
                            )}
                        />
                    </FormSection>

                    <FormSection label={_('Nombre de la actividad')}>
                        <Controller
                            name='name'
                            control={control}
                            rules={{ required: true }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField {...field} fullWidth error={!!error} />
                            )}
                        />
                    </FormSection>

                    <TagsSection
                        label={_('Temas a tratar')}
                        countLabel={_('{{count}} tema agregado', { count: topics.length })}
                        tags={topicsFieldArray.fields.map((f) => ({ id: f.id, label: f.value }))}
                        onRemove={topicsFieldArray.remove}
                        onClearAll={() => topicsFieldArray.replace([])}
                    >
                        <TextField
                            value={topicInput}
                            onChange={(e) => setTopicInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddTopic();
                                }
                            }}
                            onBlur={handleAddTopic}
                            placeholder={_('Agregar tema')}
                            fullWidth
                        />
                    </TagsSection>

                    <Controller
                        name='speakers'
                        control={control}
                        render={({ field }) => {
                            const selectedNodes = nodeOptions.filter((node) => field.value.includes(node.id));
                            const speakerOptions = (squadNodes || []).filter((node) => !field.value.includes(node.id));
                            const tags = [
                                ...selectedNodes.map((node) => ({ id: `node-${node.id}`, label: node.name })),
                                ...otherSpeakers.map((speaker, index) => ({
                                    id: `other-${index}`,
                                    label: speaker.value,
                                })),
                            ];
                            return (
                                <TagsSection
                                    label={_('Expositores')}
                                    countLabel={_('{{count}} expositor agregado', { count: tags.length })}
                                    tags={tags}
                                    onRemove={(index) => {
                                        if (index < selectedNodes.length) {
                                            const nodeId = selectedNodes[index]?.id;
                                            field.onChange(field.value.filter((id) => id !== nodeId));
                                        } else {
                                            otherSpeakersFieldArray.remove(index - selectedNodes.length);
                                        }
                                    }}
                                    onClearAll={() => {
                                        field.onChange([]);
                                        otherSpeakersFieldArray.replace([]);
                                    }}
                                >
                                    <Autocomplete
                                        options={speakerOptions}
                                        value={null}
                                        inputValue={speakerInput}
                                        onInputChange={(_event, newInputValue) => setSpeakerInput(newInputValue)}
                                        onChange={(_event, newValue: UserNode | null) => {
                                            if (!newValue) return;
                                            if (newValue.id === OTROS_SPEAKER_ID) {
                                                handleAddOtherSpeaker(newValue.name);
                                            } else {
                                                field.onChange([...field.value, newValue.id]);
                                            }
                                            setSpeakerInput('');
                                        }}
                                        getOptionLabel={(option) => option.name}
                                        filterOptions={(options, { inputValue }) => {
                                            const term = inputValue.toLowerCase().trim();
                                            if (!term) return options;
                                            const filtered = options.filter((option) =>
                                                option.name.toLowerCase().includes(term)
                                            );
                                            if (filtered.length === 0) {
                                                return [
                                                    {
                                                        id: OTROS_SPEAKER_ID,
                                                        name: inputValue.trim(),
                                                        email: '',
                                                        type: 'other',
                                                        user: null,
                                                        created: '',
                                                    } as UserNode,
                                                ];
                                            }
                                            return filtered;
                                        }}
                                        renderOption={(optionProps, option) =>
                                            option.id === OTROS_SPEAKER_ID ? (
                                                <li {...optionProps} key='otros-speaker-option'>
                                                    <Typography variant='body1' color='primary' sx={{ py: 0.5, px: 1 }}>
                                                        {option.name}
                                                    </Typography>
                                                </li>
                                            ) : (
                                                <li {...optionProps} key={option.id}>
                                                    <Stack
                                                        direction='row'
                                                        alignItems='center'
                                                        justifyContent='space-between'
                                                        spacing={1}
                                                        sx={{ width: '100%' }}
                                                    >
                                                        <Typography variant='body1'>{option.name}</Typography>
                                                        <ChipLabel
                                                            type={getNodeChipType(option.type)}
                                                            label={ROLE_LABELS[option.type]}
                                                            size='sm'
                                                        />
                                                    </Stack>
                                                </li>
                                            )
                                        }
                                        noOptionsText={_(
                                            'No se encontraron resultados. Revisa el nombre o apellido ingresado.'
                                        )}
                                        renderInput={(params) => <TextField {...params} placeholder={_('Buscar...')} />}
                                    />
                                </TagsSection>
                            );
                        }}
                    />

                    <FormSection label={_('Modalidad')}>
                        <Controller
                            name='modality'
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Stack spacing={2} sx={{ width: '100%' }}>
                                    <Stack direction='row' spacing={1}>
                                        {MODALITY_CHIP_OPTIONS.map((option) => {
                                            const selected = field.value === option.value;
                                            return (
                                                <Box
                                                    key={option.value}
                                                    onClick={() => field.onChange(option.value)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        height: '32px',
                                                        pl: 1,
                                                        pr: 1.5,
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        bgcolor: selected ? '#FFDCE4' : 'rgba(16, 56, 79, 0.12)',
                                                        color: selected ? '#DE0043' : 'primary.main',
                                                        fontSize: '14px',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {option.icon}
                                                    {option.label}
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                    {field.value === 'presencial' && (
                                        <Controller
                                            name='place'
                                            control={control}
                                            rules={{ required: true }}
                                            render={({ field: placeField, fieldState: { error } }) => (
                                                <TextField
                                                    {...placeField}
                                                    placeholder={_('Lugar')}
                                                    fullWidth
                                                    error={!!error}
                                                />
                                            )}
                                        />
                                    )}
                                </Stack>
                            )}
                        />
                    </FormSection>

                    <FormSection label={_('Presupuesto')}>
                        <Controller
                            name='budget'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' fullWidth />}
                        />
                    </FormSection>

                    <FormSection label={_('Nota')}>
                        <Controller
                            name='note'
                            control={control}
                            render={({ field }) => <TextField {...field} fullWidth multiline minRows={2} />}
                        />
                    </FormSection>
                </Stack>
            ) : (
                <Stack spacing={3} sx={{ width: '100%' }}>
                    <SelectCheckbox
                        multiple
                        alwaysOpen={false}
                        items={squadSearchItems}
                        selectedItems={Array.from(addedParticipantIds)}
                        onToggle={(item) => {
                            const node = nodesById.get(item.id);
                            if (node) toggleParticipant(node);
                        }}
                        InputProps={{ placeholder: _('Buscar por nombre') }}
                        loading={isSquadLoading}
                        onAddOther={handleAddOtherParticipant}
                        sx={{ pb: 0 }}
                    />

                    <Button
                        variant='outlined'
                        color='primary'
                        startIcon={<PersonAdd sx={{ width: 18, height: 18 }} />}
                        sx={{ alignSelf: 'flex-start' }}
                        onClick={handleAddFullTeam}
                    >
                        {_('Agregar squad completo')}
                    </Button>

                    {participants.length === 0 ? (
                        <Stack
                            alignItems='center'
                            justifyContent='center'
                            spacing={1}
                            sx={{ bgcolor: '#F5F5F5', py: 4, px: 2, minHeight: '260px' }}
                        >
                            <PersonAdd sx={{ width: 36, height: 36 }} color='primary' />
                            <Typography sx={{ fontWeight: 700, textAlign: 'center' }} color='primary'>
                                {_('Todavía no agregaste participantes')}
                            </Typography>
                            <Typography sx={{ color: 'rgba(16, 56, 79, 0.73)', textAlign: 'center' }}>
                                {_('Buscá por nombre o sumá el squad completo de una.')}
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack spacing={2} sx={{ width: '100%' }}>
                            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                                <Typography sx={{ fontSize: '12px', color: 'rgba(16, 56, 79, 0.73)' }}>
                                    {_('{{count}} participante agregado', { count: participants.length })}
                                </Typography>
                                <Stack
                                    direction='row'
                                    spacing={0.5}
                                    alignItems='center'
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => participantsFieldArray.replace([])}
                                >
                                    <DeleteOutline sx={{ width: 14, height: 14 }} />
                                    <Typography sx={{ fontSize: '12px' }} color='primary'>
                                        {_('Borrar todo')}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction='row' spacing={2}>
                                {attendanceSummary.map((item) => (
                                    <Typography sx={{ fontSize: '12px', color: item.color }} key={item.label}>
                                        <Box component='span' fontWeight={700}>
                                            {item.value}
                                        </Box>{' '}
                                        {item.label}
                                    </Typography>
                                ))}
                            </Stack>

                            <Stack spacing={1.5}>
                                {participantsFieldArray.fields.map((participantField, index) => {
                                    const node =
                                        participantField.userNode !== null
                                            ? nodesById.get(participantField.userNode)
                                            : undefined;
                                    if (!node && !participantField.otherName) {
                                        // Still resolving the node lookup — show a placeholder instead of
                                        // silently dropping the row so the count above stays accurate.
                                        if (isNodesLoading) {
                                            return <Skeleton key={participantField.id} variant='rounded' height={96} />;
                                        }
                                        return null;
                                    }
                                    return (
                                        <ParticipantCard
                                            key={participantField.id}
                                            control={control}
                                            index={index}
                                            node={node ?? null}
                                            otherName={participantField.otherName}
                                            squad={participantField.squad}
                                            onRemove={() => participantsFieldArray.remove(index)}
                                        />
                                    );
                                })}
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            )}
        </ModalV2>
    );
};

export default ActivityFormModal;
